import * as React from "react";
import Color, { TSColor } from "../../model/Color";
import TextFlowObject from "../../model/TextFlowObject";
import ShapeFlowObject from "../../model/ShapeFlowObject";
import Popup from "reactjs-popup";
import { MachineContext } from "../fsm/useMachine";
import { SET_COLOR, SET_GRADIENT, DELEGATE } from "../fsm/types";
import { useEditorCoordinator } from "../text_editor/editorCoordinator";
import Tooltip from "@material-ui/core/Tooltip";
import { GradientDef } from "../../ai/styles/themeDecorations";


export const ColorPicker = () => {
    const [current, send] = React.useContext(MachineContext);
    const { currentObject, flow, index } = current.context
    const [displayColor, setDisplayColor] = React.useState(new TSColor(255, 255, 255, 1));
    const { globalEditor } = useEditorCoordinator()
    const [globalEditorVal,] = globalEditor

    // Gradient state — only relevant when a shape is selected
    const isShape = currentObject?.type === "shape";
    const shapeMmo = isShape ? (currentObject as ShapeFlowObject) : null;
    const [gradientEnabled, setGradientEnabled] = React.useState(false);
    const [gradientType, setGradientType] = React.useState<'linear' | 'radial'>('linear');
    const [gradientAngle, setGradientAngle] = React.useState(180);
    const [gradientStartColor, setGradientStartColor] = React.useState('#3B82F6');
    const [gradientEndColor, setGradientEndColor] = React.useState('#8B5CF6');
    const [gradientOpacity, setGradientOpacity] = React.useState(1);

    // Sync gradient state from selected shape
    React.useEffect(() => {
        if (shapeMmo?.gradientDef) {
            setGradientEnabled(true);
            setGradientType(shapeMmo.gradientDef.type);
            setGradientAngle(shapeMmo.gradientDef.angle ?? 180);
            setGradientOpacity(shapeMmo.gradientDef.opacity ?? 1);
            if (shapeMmo.gradientDef.stops.length >= 2) {
                setGradientStartColor(shapeMmo.gradientDef.stops[0].color);
                setGradientEndColor(shapeMmo.gradientDef.stops[shapeMmo.gradientDef.stops.length - 1].color);
            }
        } else {
            setGradientEnabled(false);
            setGradientOpacity(1);
        }
    }, [currentObject]);

    /**
   * Converts Hex to TSColor
   * @param color: Object made of hex, hsl, and rgb pieces.
   * @returns {TSColor} Color
   */
    const colorToTSColor = (color: string) => {
        return TSColor.fromHex(color)
    }

    /**
     * Set this objects color to selected one.
     * @param color: react-color color object (access to rgb, hsl, and hex)
     * @param {boolean} alphaOnly only change alpha value
     */
    const changeColor = (color: string, alphaOnly: boolean = false) => {
        const { currentObject } = current.context;
        let colour: Color;
        if (globalEditorVal && currentObject?.type === "text") {
            if (alphaOnly) {
                if (!globalEditorVal.isEditable) {
                    globalEditorVal.commands.setTextSelection({from: 0, to: (currentObject! as TextFlowObject).text.length + 1})
                }
                const newcolor = displayColor.setA(parseFloat(color)).asRgba()
                globalEditorVal.chain().focus().setFontColor(newcolor).run()
            } else {
                if (!globalEditorVal.isEditable) {
                    globalEditorVal.commands.setTextSelection({from: 0, to: (currentObject! as TextFlowObject).text.length + 1})
                }
                globalEditorVal.chain().focus().setFontColor(Color.fromHex(color).asRgba()).run()
            }
        }
        else if (currentObject) {
            if (alphaOnly) {
                colour = displayColor.setA(parseFloat(color))
                send({ type: DELEGATE, payload: { type: SET_COLOR, color: colour }, id: currentObject.id })
            } else {
                colour = colorToTSColor(color)
                send({ type: DELEGATE, payload: { type: SET_COLOR, color: colour }, id: currentObject.id })
            }
            setDisplayColor(colour!)
        } else {
            if (alphaOnly) {
                colour = displayColor.setA(parseFloat(color))
            } else {
                colour = colorToTSColor(color)
            }
            send({ type: SET_COLOR, color: colour })
            setDisplayColor(colour!)
        }
    };

    const applyGradient = (
        type: 'linear' | 'radial' = gradientType,
        angle: number = gradientAngle,
        startColor: string = gradientStartColor,
        endColor: string = gradientEndColor,
        opacity: number = gradientOpacity
    ) => {
        if (!currentObject || currentObject.type !== "shape") return;
        const gradient: GradientDef = {
            type,
            angle,
            opacity,
            stops: [
                { color: startColor, offset: 0 },
                { color: endColor, offset: 100 },
            ],
        };
        send({ type: DELEGATE, payload: { type: SET_GRADIENT, gradient }, id: currentObject.id });
    };

    const removeGradient = () => {
        if (!currentObject || currentObject.type !== "shape") return;
        send({ type: DELEGATE, payload: { type: SET_GRADIENT, gradient: null }, id: currentObject.id });
    };

    const handleGradientToggle = () => {
        if (gradientEnabled) {
            setGradientEnabled(false);
            removeGradient();
        } else {
            setGradientEnabled(true);
            applyGradient();
        }
    };

    React.useEffect(() => {
        if (!currentObject) {
            setDisplayColor(colorOrDefault(flow.screens[index], "FlowScreenModelBackgroundColorKey"))
        }
        else if (currentObject!.type == "text") {
            let color: Color;
            if (globalEditorVal && globalEditorVal.getMarkAttributes) {
                const fontColor = Color.fromRgbaString(globalEditorVal.getMarkAttributes("textStyle").fontColor)
                color = fontColor
            } else {
                color = (currentObject as TextFlowObject)!.attributedString_flbAttributesArray[0].color!;
            }
            setDisplayColor(color)
        } else if (currentObject!.type == "shape") {
            setDisplayColor((currentObject as ShapeFlowObject)!.color);
        }
        // console.log(JSON.stringify(defaultColor, replacer));
    }, [current.context.currentObject, colorOrDefault(current.context.flow.screens[current.context.index], "FlowScreenModelBackgroundColorKey")])

    const effectiveOpacity = gradientEnabled ? gradientOpacity : displayColor.a;
    let displayAlpha = "0"
    if (effectiveOpacity == 1.0) {
        displayAlpha = "100"
    } else {
        displayAlpha = (effectiveOpacity * 100).toPrecision(2)
    }

    // Build gradient preview CSS for the color swatch trigger
    const gradientPreviewCss = gradientEnabled
        ? gradientType === 'linear'
            ? `linear-gradient(${gradientAngle}deg, ${gradientStartColor}, ${gradientEndColor})`
            : `radial-gradient(circle, ${gradientStartColor}, ${gradientEndColor})`
        : undefined;

    return (

        <Popup
            trigger={
                <Tooltip title="Set Color" className="buttonControlsMarginTop">
                {gradientPreviewCss ? (
                    <div
                        className="buttonControlsColor color_swatch"
                        style={{
                            background: gradientPreviewCss,
                            width: 28,
                            height: 28,
                            borderRadius: 4,
                            border: '1px solid #ccc',
                            cursor: 'pointer',
                            display: 'inline-block',
                        }}
                    />
                ) : (
                    <input type="color" className="buttonControlsColor color_swatch " value={displayColor.asHex(false)} onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        changeColor(e.target.value)
                    }}
                    />
                )}
                </Tooltip>
            }
            on="click"
            position={gradientEnabled ? "right center" : "right bottom"}
            keepTooltipInside="body"
            closeOnDocumentClick={true}
            closeOnEscape={true}
            className="opacity-picker"

        >
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <h4>Adjust Opacity: {displayAlpha}%</h4>
                &nbsp;
                <input className="opacity_slider" type="range" min="0" max="1.0" step="0.1"
                    value={effectiveOpacity.toPrecision(2)}
                    onChange={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (gradientEnabled) {
                            const newOpacity = parseFloat(e.target.value);
                            setGradientOpacity(newOpacity);
                            applyGradient(gradientType, gradientAngle, gradientStartColor, gradientEndColor, newOpacity);
                        } else {
                            changeColor(e.target.value, true)
                        }
                    }}

                />

                {isShape && (
                    <div style={{ marginTop: 12, borderTop: '1px solid #ddd', paddingTop: 8 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
                            <input
                                type="checkbox"
                                checked={gradientEnabled}
                                onChange={handleGradientToggle}
                            />
                            Gradient
                        </label>

                        {gradientEnabled && (
                            <div style={{ marginTop: 8 }}>
                                {/* Type toggle */}
                                <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                                    <button
                                        onClick={() => { setGradientType('linear'); applyGradient('linear'); }}
                                        style={{
                                            flex: 1, padding: '3px 8px', fontSize: 12, cursor: 'pointer',
                                            border: '1px solid #ccc', borderRadius: 3,
                                            background: gradientType === 'linear' ? '#4A90D9' : '#fff',
                                            color: gradientType === 'linear' ? '#fff' : '#333',
                                        }}
                                    >Linear</button>
                                    <button
                                        onClick={() => { setGradientType('radial'); applyGradient('radial'); }}
                                        style={{
                                            flex: 1, padding: '3px 8px', fontSize: 12, cursor: 'pointer',
                                            border: '1px solid #ccc', borderRadius: 3,
                                            background: gradientType === 'radial' ? '#4A90D9' : '#fff',
                                            color: gradientType === 'radial' ? '#fff' : '#333',
                                        }}
                                    >Radial</button>
                                </div>

                                {/* Color pickers */}
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                                    <label style={{ fontSize: 11, flex: 1 }}>
                                        Start
                                        <input type="color" value={gradientStartColor} style={{ display: 'block', width: '100%', height: 24, padding: 0, border: '1px solid #ccc', cursor: 'pointer' }}
                                            onChange={(e) => { setGradientStartColor(e.target.value); applyGradient(gradientType, gradientAngle, e.target.value, gradientEndColor); }}
                                        />
                                    </label>
                                    <label style={{ fontSize: 11, flex: 1 }}>
                                        End
                                        <input type="color" value={gradientEndColor} style={{ display: 'block', width: '100%', height: 24, padding: 0, border: '1px solid #ccc', cursor: 'pointer' }}
                                            onChange={(e) => { setGradientEndColor(e.target.value); applyGradient(gradientType, gradientAngle, gradientStartColor, e.target.value); }}
                                        />
                                    </label>
                                </div>

                                {/* Angle slider (only for linear) */}
                                {gradientType === 'linear' && (
                                    <div>
                                        <label style={{ fontSize: 11 }}>Angle: {gradientAngle}&deg;</label>
                                        <input type="range" min="0" max="360" step="1" value={gradientAngle}
                                            style={{ width: '100%' }}
                                            onChange={(e) => {
                                                const angle = parseInt(e.target.value, 10);
                                                setGradientAngle(angle);
                                                applyGradient('linear', angle);
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Live preview strip */}
                                <div style={{
                                    marginTop: 6,
                                    height: 20,
                                    borderRadius: 3,
                                    border: '1px solid #ccc',
                                    background: gradientType === 'linear'
                                        ? `linear-gradient(${gradientAngle}deg, ${gradientStartColor}, ${gradientEndColor})`
                                        : `radial-gradient(circle, ${gradientStartColor}, ${gradientEndColor})`,
                                }} />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Popup>
    )
}

ColorPicker.displayName = "ColorPicker"

function colorOrDefault(object: any, key: string, defaultValue = Color.default()): Color {
    if (object) {
        return object[key]
    } else {
        return defaultValue
    }
}
